/**
 * The chat composer's attachments (t509u: shared with "Message people on shifts"): pick or paste files, each uploads
 * straight away through uploadFile (the shared allow-list and 50 MB limit, the same storage), shown as a chip with a
 * spinner while uploading, its name and size, and an ✕ to take it off. Only finished uploads are sent.
 */
import { useRef, useState } from 'react';
import clsx from 'clsx';
import { FileText, Loader2, Paperclip, X } from 'lucide-react';
import { UPLOAD_ACCEPT, uploadFailureText, type AttachmentSummary } from '@tradeoutlet/shared';
import { errorMessage } from '@/lib/api';
import { fileSize } from '@/lib/format';
import { useUi } from '@/lib/store';
import { uploadFile } from '@/lib/upload';

export interface ComposerUpload { key: string; name: string; size: number; status: 'uploading' | 'ready' | 'failed'; attachment?: AttachmentSummary; error?: string }
export type UploadTarget = Parameters<typeof uploadFile>[1];

export function useComposerUploads(target: () => UploadTarget | null, opts: { onRemove?: (u: ComposerUpload) => void } = {}) {
  const [uploads, setUploads] = useState<ComposerUpload[]>([]);
  const toast = useUi(s => s.toast);
  const addFiles = (files: FileList | File[]) => {
    const t = target();
    if (!t) return;
    for (const file of Array.from(files)) {
      const key = crypto.randomUUID();
      setUploads(u => [...u, { key, name: file.name, size: file.size, status: 'uploading' }]);
      uploadFile(file, t)
        .then(a => setUploads(u => u.map(x => (x.key === key ? { ...x, status: 'ready', attachment: a } : x))))
        .catch(e => {
          setUploads(u => u.map(x => (x.key === key ? { ...x, status: 'failed', error: errorMessage(e) } : x)));
          toast(uploadFailureText(errorMessage(e), 'Upload failed'), 'error');
        });
    }
  };
  const remove = (key: string) => setUploads(x => {
    const u = x.find(y => y.key === key);
    if (u) opts.onRemove?.(u);
    return x.filter(y => y.key !== key);
  });
  return {
    uploads, addFiles, remove, clear: () => setUploads([]),
    busy: uploads.some(u => u.status === 'uploading'),
    ready: uploads.filter(u => u.status === 'ready' && u.attachment).map(u => u.attachment!),
    /** Paste a screenshot or file straight in. */
    onPaste: (e: React.ClipboardEvent) => { if (e.clipboardData.files.length) { e.preventDefault(); addFiles(e.clipboardData.files); } },
  };
}

export function UploadChips({ uploads, onRemove, className }: { uploads: ComposerUpload[]; onRemove: (key: string) => void; className?: string }) {
  if (!uploads.length) return null;
  return (
    <div className={clsx('flex flex-wrap gap-2', className)} data-testid="upload-chips">
      {uploads.map(u => (
        <div key={u.key} data-testid="upload-chip" data-status={u.status} title={u.error}
          className={clsx('flex items-center gap-2 rounded-lg border px-2 py-1.5 text-xs', u.status === 'failed' ? 'border-rose-300 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-900/40 dark:text-rose-200' : 'border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800')}>
          {u.status === 'uploading' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
          <span className="max-w-40 truncate">{u.name}</span>
          <span className="text-slate-500">{fileSize(u.size)}</span>
          <button type="button" aria-label={`Remove ${u.name}`} onClick={() => onRemove(u.key)}><X className="h-3.5 w-3.5" /></button>
        </div>
      ))}
    </div>
  );
}

/** The paperclip and its hidden file input. */
export function AttachButton({ onFiles, className }: { onFiles: (files: FileList) => void; className?: string }) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <>
      <button type="button" title="Attach files" aria-label="Attach files" data-testid="attach-button" onClick={() => ref.current?.click()}
        className={clsx('rounded p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800', className)}><Paperclip className="h-4 w-4" /></button>
      <input ref={ref} type="file" accept={UPLOAD_ACCEPT} multiple hidden data-testid="attach-input" onChange={e => { if (e.target.files) onFiles(e.target.files); e.target.value = ''; }} />
    </>
  );
}
